import ActiveIcon from '@/assets/icons/ActiveIcon';
import GoogleIcon from '@/assets/icons/GoogleIcon';
import InactiveIcon from '@/assets/icons/InactiveIcon';
import LineIcon from '@/assets/icons/LineIcon';
import PassKeyIcon from '@/assets/icons/PassKeyIcon';
import TwitterIcon from '@/assets/icons/TwitterIcon';
import { Button } from '@/components/common/Button';
import Combobox from '@/components/common/Combobox';
import { Form, FormControl, FormField, FormItem } from '@/components/common/Form';
import { Label } from '@/components/common/Label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/common/Tabs';
import Table from '@/components/table/Table';
import { ACCOUNT_TYPES, DEFAULT_SELECT_PAGE_SIZES, STATUS_TYPES } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { ACCOUNT_TYPE, ROLE, STATUS_TYPE } from '@/types/enums';
import { toastify } from '@/utils/toastify';
import { convertDate } from '@/utils/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { ColumnDef, TableState, Updater } from '@tanstack/react-table';
import { Search, UserPlus, QrCode } from 'lucide-react';
import { flowResult, toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import CreateBusinessUserSheet from './components/CreateBusinessUserSheet';
import BusinessLoginQrModal from './components/BusinessLoginQrModal';

export default observer(function UserManagementPage() {
    // hooks
    const { t } = useTranslation();

    // store
    const {
        userStore: { getUsers, clean, changeStatusUser, changeRoleUser, changeTesterUser, users, paging, defaultPaging, totalRecord, keepSearchParams, setObservable },
        modalStore: { showAlertModal }
    } = useStore();

    // state
    const [rowSelected, setRowSelected] = useState({});
    const [activeTab, setActiveTab] = useState<string>(ROLE.END_USER.toString());
    // Pattern A (2026-05-15): BUSINESS user 直接作成 sheet
    const [createBusinessOpen, setCreateBusinessOpen] = useState(false);
    // 2026-05-16 (B2B onboarding fix): ログイン QR modal
    const [qrOpen, setQrOpen] = useState(false);
    const [qrUsername, setQrUsername] = useState<string | null>(null);

    // lifecycle
    useEffect(() => {
        form.setValue('status', undefined);
        form.setValue('accountType', undefined);
        form.setValue('isTester', undefined);
        getUsers({ roleId: activeTab }, defaultPaging);
    }, [activeTab]);

    // function
    const onSearch = async (searchParams?: any) => {
        setObservable('keepSearchParams', searchParams);
        await flowResult(getUsers({ ...searchParams, roleId: activeTab }, defaultPaging));
    };

    const onStateChange = (stateUpdater: Updater<TableState>) => {
        const newPaging = stateUpdater instanceof Function ? stateUpdater(paging) : stateUpdater;
        getUsers({ ...keepSearchParams, roleId: activeTab }, newPaging);
    };
    const validateSchema = yup.object().shape({
        status: yup.string(),
        accountType: yup.string(),
        // テスターで絞り込む (2026-11-04 §50)。'true' / 'false' / 未指定 = すべて
        isTester: yup.string()
    });
    type FormData = yup.InferType<typeof validateSchema>;
    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        defaultValues: {
            status: keepSearchParams?.status ?? undefined,
            accountType: keepSearchParams?.accountType ?? undefined,
            isTester: keepSearchParams?.isTester ?? undefined
        }
    });

    // columns Admin
    const columnsAdmin: ColumnDef<any>[] = [
        {
            accessorKey: 'id',
            header: 'No.',
            size: 50,
            enableSorting: false,
            cell: ({ row }) => {
                const { pageIndex, pageSize } = paging.pagination;
                const index = (row.index + 1) + (pageIndex * pageSize);
                return index;
            }
        },
        {
            // 実 user.id (= landmark「スポンサー事業者」で紐付ける値)。No. は表示連番で ID とは別。
            accessorKey: 'realId',
            header: 'ID',
            size: 60,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='font-mono text-gray-600'>{original.id}</div>
            )
        },
        {
            accessorKey: 'username',
            header: t('words_title.username'),
            size: 400,
            enableSorting: false,
            cell: ({ row: { original } }) => {
                return (
                    <div className=''>{original.username}</div>
                );
            }

        },
        {
            accessorKey: 'email',
            header: t('words_title.email'),
            size: 650,
            enableSorting: false,
            cell: ({ row: { original } }) => {
                return (
                    <div className=''>{original.email}</div>
                );
            }
        },
        {
            accessorKey: 'createdDate',
            header: t('words_title.create_date'),
            size: 400,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='flex space-x-2'>
                    <div>
                        {convertDate(original?.createdDate)?.date}
                    </div>
                    <div>
                        {convertDate(original?.createdDate)?.time}
                        <span className='text-timezone ml-1'>{convertDate(original?.createdDate)?.ampm}</span>
                    </div>
                </div>
            )
        },
        {
            accessorKey: 'status',
            header: t('words_title.status'),
            size: 300,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <>
                    {
                        original.status === STATUS_TYPE.ACTIVE ?
                            (
                                <div className='flex items-center'>
                                    <div className='size-[9px] bg-gradient-to-r from-background-linear-1 to-background-linear-2 rounded-full mr-1'></div>
                                    <span>{t('field.active')}</span>
                                </div>
                            ) :
                            (
                                <div className='flex items-center'>
                                    <div className='size-[9px] bg-gradient-to-r from-background-linear-1 to-background-linear-2 rounded-full mr-1'></div>
                                    <span>{t('field.inactive')}</span>
                                </div>
                            )

                    }
                </>
            )
        }
    ];

    // colums User
    const columnsUser: ColumnDef<any>[] = [
        {
            accessorKey: 'id',
            header: 'No.',
            size: 50,
            enableSorting: false,
            cell: ({ row }) => {
                const { pageIndex, pageSize } = paging.pagination;
                const index = (row.index + 1) + (pageIndex * pageSize);
                return index;
            }
        },
        {
            // 実 user.id (= landmark「スポンサー事業者」で紐付ける値)。No. は表示連番で ID とは別。
            accessorKey: 'realId',
            header: 'ID',
            size: 60,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='font-mono text-gray-600'>{original.id}</div>
            )
        },
        {
            accessorKey: 'username',
            header: t('words_title.username'),
            size: 400,
            enableSorting: false,
            cell: ({ row: { original } }) => {
                return (
                    <div className=''>
                        {original.username}
                        {original.isTester && (
                            <span
                                className='ml-2 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800'
                                title={original.testerNote || undefined}
                            >
                                テスター
                            </span>
                        )}
                    </div>
                );
            }

        },
        {
            accessorKey: 'type',
            header: t('words_title.account_type'),
            size: 400,
            enableSorting: false,
            cell: ({ row: { original } }) => {
                const Icon = ICON_MAP[original?.type]?.icon;
                return (
                    <div className='flex items-center space-x-1.5'>
                        {
                            Icon && <Icon />
                        }
                    </div>
                );
            }

        },
        {
            accessorKey: 'email',
            header: t('words_title.email'),
            size: 650,
            enableSorting: false,
            cell: ({ row: { original } }) => {
                return (
                    <div className=''>{original.email}</div>
                );
            }
        },
        {
            accessorKey: 'createdDate',
            header: t('words_title.create_date'),
            size: 600,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='flex space-x-2'>
                    <div>
                        {convertDate(original?.createdDate)?.date}
                    </div>
                    <div>
                        {convertDate(original?.createdDate)?.time}
                        <span className='text-timezone ml-1'>{convertDate(original?.createdDate)?.ampm}</span>
                    </div>
                </div>
            )
        },
        {
            accessorKey: 'status',
            header: t('words_title.status'),
            size: 400,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <>
                    {
                        original.status === STATUS_TYPE.ACTIVE ?
                            (
                                <div className='flex items-center'>
                                    <div className='size-[9px] bg-gradient-to-r from-background-linear-1 to-background-linear-2 rounded-full mr-1'></div>
                                    <span>{t('field.active')}</span>
                                </div>
                            ) :
                            (
                                <div className='flex items-center'>
                                    <div className='size-[9px] bg-gradient-to-r from-[#909090] to-[#A7A7A7] rounded-full mr-1'></div>
                                    <span>{t('field.inactive')}</span>
                                </div>
                            )

                    }
                </>
            )
        },
        {
            accessorKey: 'action',
            header: t('words_title.action'),
            size: 200,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <>
                    <Button
                        onClick={() => {
                            handleChangeStatusUser(original.id, original.username, original.status);
                        }}
                        size='icon'
                        variant='ghost'
                        className={`${original.status === STATUS_TYPE.ACTIVE ? 'bg-background-inactive' : 'bg-background-active'} rounded-full`}
                    >

                        {
                            original.status === STATUS_TYPE.ACTIVE ?
                                (
                                    <InactiveIcon />
                                ) :
                                (
                                    <ActiveIcon />
                                )
                        }
                    </Button>
                </>
            )
        },
        {
            // U2 (2026-05-06): ロール変更ボタン。END_USER タブでは BUSINESS 昇格、BUSINESS タブでは END_USER へ戻す。
            // ADMIN ロールはこの UI で変更不可 (= 安全のため、columnsAdmin にこの列を載せない)。
            accessorKey: 'roleAction',
            header: 'テスター / ロール変更 / QR',
            size: 480,
            enableSorting: false,
            cell: ({ row: { original } }) => {
                const isBusinessTab = activeTab === ROLE.BUSINESS.toString();
                const targetRole = isBusinessTab ? ROLE.END_USER : ROLE.BUSINESS;
                const label = isBusinessTab ? 'BUSINESS 解除' : '事業者へ昇格';
                return (
                    <div className='flex gap-2'>
                        {/* テスターの印 (2026-11-04 §50) */}
                        <Button
                            onClick={() => handleChangeTesterUser(original.id, original.username, !original.isTester)}
                            size='sm'
                            variant='outline'
                            title='テスターは「限定公開 (テスター)」のスポットが地図に出て、GPS で引けます'
                        >
                            {original.isTester ? 'テスター解除' : 'テスターにする'}
                        </Button>
                        <Button
                            onClick={() => handleChangeRoleUser(original.id, original.username, targetRole)}
                            size='sm'
                            variant='outline'
                        >
                            {label}
                        </Button>
                        {/* 2026-05-16 (B2B onboarding fix): 「事業者」 タブのみ「ログイン QR」 button */}
                        {isBusinessTab && (
                            <Button
                                onClick={() => {
                                    setQrUsername(original.username);
                                    setQrOpen(true);
                                }}
                                size='sm'
                                variant='outline'
                                title='事業者向け enduser PWA ログイン QR を表示'
                            >
                                <QrCode className='w-4 h-4 mr-1' />
                                ログイン QR
                            </Button>
                        )}
                    </div>
                );
            }
        }
    ];

    // function
    const handleTabChange = (tabValue: string) => {
        setActiveTab(tabValue);
    };

    const handleChangeStatusUser = (id: string, username: string, status: number) => {
        showAlertModal({
            title: '確認',
            size: 'sm',
            headerClassName: 'border-b border-b-border-7',
            content: status === STATUS_TYPE.ACTIVE ? t('sentences.confirm_inactive_user') : t('sentences.confirm_active_user'),
            onSave: async () => {
                const res = await flowResult(changeStatusUser({
                    id, payload: {
                        status: status === STATUS_TYPE.ACTIVE ? STATUS_TYPE.INACTIVE : STATUS_TYPE.ACTIVE
                    }
                }));
                if (res) {
                    toastify('success', status === STATUS_TYPE.ACTIVE ? t('messages.active_success', { username }) : t('messages.inactive_success', { username }));
                    getUsers({ ...keepSearchParams, roleId: activeTab }, paging);
                }
            }
        });
    };

    // U2 (2026-05-06): MEO landmark ads PoC で BUSINESS 付与 / 剥奪する。
    // ADMIN ロールはこの UI で変更しない (= 自身の lockout 防止 + 安全)。
    const handleChangeRoleUser = (id: string, username: string, targetRole: number) => {
        const targetLabel = targetRole === ROLE.BUSINESS ? '事業者 (BUSINESS)' : 'クライアント (END_USER)';
        showAlertModal({
            title: 'ロール変更確認',
            size: 'sm',
            headerClassName: 'border-b border-b-border-7',
            content: `${username} を ${targetLabel} に変更しますか?`,
            onSave: async () => {
                const res: any = await flowResult(changeRoleUser({
                    id, payload: { role_id: targetRole }
                }));
                if (res?.ok) {
                    toastify('success', `${username} のロールを ${targetLabel} に変更しました`);
                    getUsers({ ...keepSearchParams, roleId: activeTab }, paging);
                } else {
                    toastify('error', res?.message || 'ロール変更に失敗しました');
                }
            }
        });
    };

    // テスターの印の付け外し (2026-11-04 §50)
    const handleChangeTesterUser = (id: string, username: string, toTester: boolean) => {
        showAlertModal({
            title: 'テスター変更確認',
            size: 'sm',
            headerClassName: 'border-b border-b-border-7',
            content: toTester ?
                `${username} をテスターにしますか? 「限定公開 (テスター)」のスポットが地図に出て、GPS で引けるようになります。` :
                `${username} のテスターを解除しますか?`,
            onSave: async () => {
                const res: any = await flowResult(changeTesterUser({ id, payload: { is_tester: toTester } }));
                if (res?.ok) {
                    toastify('success', toTester ? `${username} をテスターにしました` : `${username} のテスターを解除しました`);
                    getUsers({ ...keepSearchParams, roleId: activeTab }, paging);
                } else {
                    toastify('error', res?.message || 'テスターの変更に失敗しました');
                }
            }
        });
    };

    return (
        <>
            <div className='layout-card'>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSearch)}>
                        <div className='grid grid-cols-1 md:grid-cols-2 gap-8'>
                            <div className='flex items-center gap-4'>
                                <Label className='text-[13px]'>{t('words_title.status')}</Label>
                                <FormField
                                    control={form.control}
                                    name='status'
                                    render={({ field }) => (
                                        <FormItem className='flex-1 w-full'>
                                            <FormControl>
                                                <Combobox
                                                    className='h-[3.25rem]'
                                                    placeholder={t('words_title.all')}
                                                    value={String(field.value)}
                                                    onChange={(item) => {
                                                        field.onChange(item?.value);
                                                        form.setValue('status', item?.code);
                                                    }}
                                                    items={
                                                        [{ id: undefined, name: 'field.all' }, ...STATUS_TYPES].map(item => ({
                                                            label: t(`${item.name}`),
                                                            value: String(item?.id),
                                                            code: item.id
                                                        }))
                                                    }
                                                />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                            </div>
                            <div className='flex items-center gap-4'>
                                <Label className='text-[13px]'>{t('words_title.account_type')}</Label>
                                <FormField
                                    control={form.control}
                                    name='accountType'
                                    render={({ field }) => (
                                        <FormItem className='flex-1 w-full'>
                                            <FormControl>
                                                <Combobox
                                                    className='h-[3.25rem]'
                                                    placeholder={t('words_title.all')}
                                                    value={String(field.value)}
                                                    onChange={(item) => {
                                                        field.onChange(item?.value);
                                                        form.setValue('accountType', item?.code);
                                                    }}
                                                    items={
                                                        [{ id: undefined, name: 'field.all' }, ...ACCOUNT_TYPES].map(item => ({
                                                            label: t(`${item.name}`),
                                                            value: String(item?.id),
                                                            code: item.id
                                                        }))
                                                    }
                                                />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                            </div>
                            {/* テスターで絞り込む (2026-11-04 §50) */}
                            <div className='flex items-center gap-4'>
                                <Label className='text-[13px]'>テスター</Label>
                                <FormField
                                    control={form.control}
                                    name='isTester'
                                    render={({ field }) => (
                                        <FormItem className='flex-1 w-full'>
                                            <FormControl>
                                                <Combobox
                                                    className='h-[3.25rem]'
                                                    placeholder={t('words_title.all')}
                                                    value={String(field.value)}
                                                    onChange={(item) => {
                                                        field.onChange(item?.value);
                                                        form.setValue('isTester', item?.code);
                                                    }}
                                                    items={[
                                                        { label: t('field.all'), value: 'undefined', code: undefined },
                                                        { label: 'テスターのみ', value: 'true', code: 'true' },
                                                        { label: 'テスター以外', value: 'false', code: 'false' }
                                                    ]}
                                                />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                            </div>
                        </div>
                        <div className='mt-6 flex justify-center items-center'>
                            <Button size='xl' variant='active' className='h-[3.25rem] w-[13.5rem] flex items-center space-x-2'>
                                <Search className='size-[22px]' />
                                <p className='text-[16px] font-semibold'>{t('button.search')}</p>
                            </Button>
                        </div>
                    </form>
                </Form>
            </div>
            <div className='layout-card pt-2 mt-6'>
                {/* Pattern A (2026-05-15): BUSINESS user を直接作成する button。
                    既存「事業者へ昇格」 (= 各 row の roleAction button) と並走、B2B 営業現場で
                    credential 即発行するための導線。 */}
                <div className='flex justify-end mb-2'>
                    <Button
                        variant='active'
                        onClick={() => setCreateBusinessOpen(true)}
                        className='flex items-center gap-2'
                    >
                        <UserPlus className='w-4 h-4' />
                        事業者を新規追加
                    </Button>
                </div>

                <Tabs value={activeTab} onValueChange={handleTabChange} className='w-full h-full flex flex-col'>
                    <div className='bg-white rounded'>
                        {/* U1 (2026-05-06): MEO landmark ads PoC で 事業者ロール (BUSINESS=2) を運用するため
                            「事業者」タブを追加。backend (UserService.findAllItem) は roleId クエリで filter 済。 */}
                        <TabsList variant='userTab' data-orientation='horizontal' className='flex w-[75%]'>
                            <TabsTrigger
                                className='text-text-black-1 w-full'
                                value={ROLE.END_USER.toString()}
                                variant='userTab'
                            >
                                {t('words_title.client')}
                            </TabsTrigger>
                            <TabsTrigger
                                className='text-text-black-1 w-full'
                                value={ROLE.ADMIN.toString()}
                                variant='userTab'
                            >
                                {t('words_title.admin')}
                            </TabsTrigger>
                            <TabsTrigger
                                className='text-text-black-1 w-full'
                                value={ROLE.BUSINESS.toString()}
                                variant='userTab'
                            >
                                事業者
                            </TabsTrigger>
                        </TabsList>
                    </div>

                    <div className='bg-white rounded py-6'>
                        <TabsContent value={ROLE.END_USER.toString()}>
                            <Table
                                columns={columnsUser}
                                data={users}
                                state={{
                                    ...paging,
                                    rowSelection: rowSelected
                                }}
                                rowCount={totalRecord}
                                onStateChange={onStateChange}
                                onRowSelectionChange={setRowSelected}

                            />
                        </TabsContent>
                        <TabsContent value={ROLE.ADMIN.toString()}>
                            <Table
                                columns={columnsAdmin}
                                data={users}
                                state={{
                                    ...paging,
                                    rowSelection: rowSelected
                                }}
                                rowCount={totalRecord}
                                onStateChange={onStateChange}
                                selectPageSizes={DEFAULT_SELECT_PAGE_SIZES}
                                onRowSelectionChange={setRowSelected}
                                getRowId={row => row.id?.toString()}
                            />
                        </TabsContent>
                        <TabsContent value={ROLE.BUSINESS.toString()}>
                            {/* BUSINESS タブ: columnsUser を流用 (= END_USER と同じ social/passkey login 経路想定)。
                                role 変更 UI (= U2) は別 PR で追加予定。 */}
                            <Table
                                columns={columnsUser}
                                data={users}
                                state={{
                                    ...paging,
                                    rowSelection: rowSelected
                                }}
                                rowCount={totalRecord}
                                onStateChange={onStateChange}
                                onRowSelectionChange={setRowSelected}
                            />
                        </TabsContent>
                    </div>
                </Tabs>
            </div>

            {/* Pattern A (2026-05-15): BUSINESS user 直接作成 sheet */}
            <CreateBusinessUserSheet
                open={createBusinessOpen}
                onOpenChange={setCreateBusinessOpen}
                onSuccess={() => getUsers({ ...keepSearchParams, roleId: ROLE.BUSINESS.toString() }, defaultPaging)}
            />

            {/* 2026-05-16 (B2B onboarding fix): 事業者ログイン QR modal */}
            <BusinessLoginQrModal
                open={qrOpen}
                onOpenChange={setQrOpen}
                username={qrUsername}
            />
        </>

    );
});

export const ICON_MAP = {
    [ACCOUNT_TYPE.GOOGLE]: {
        icon: GoogleIcon,
        name: 'field.google'
    },
    [ACCOUNT_TYPE.LINE]: {
        icon: LineIcon,
        name: 'field.line'
    },
    [ACCOUNT_TYPE.TWITTER]: {
        icon: TwitterIcon,
        name: 'field.twitter'
    },
    [ACCOUNT_TYPE.PASSKEY]: {
        icon: PassKeyIcon,
        name: 'field.passkey2'
    }
};
